import React, { useEffect, useState } from "react";
import { updateBooking, updateBookingStatus, addBookingPayment, deleteBooking, getTeam } from "@/api";
import { STATUSES, EXTRA_LABELS, PAYMENT_METHODS, fmtMxn, StatusBadge, inputCls } from "./adminShared";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MessageCircle, Wallet, Ban, Trash2, Save } from "lucide-react";

const OPS_FIELDS = [
  { key: "guide", label: "Guía" },
  { key: "photographer", label: "Fotógrafo" },
  { key: "transport", label: "Transporte" },
  { key: "op_schedule", label: "Horario de operación" },
  { key: "meeting_point", label: "Punto de encuentro" },
];

export default function BookingDetail({ booking, open, onClose, onUpdated }) {
  const [ops, setOps] = useState({});
  const [notes, setNotes] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("transferencia");
  const [payNote, setPayNote] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // "cancel" | "delete"
  const [team, setTeam] = useState([]);

  useEffect(() => {
    if (booking) {
      setOps(booking.ops || {});
      setNotes(booking.internal_notes || "");
      getTeam().then(setTeam).catch(() => {});
    }
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!booking) return null;

  const b = booking;
  const balance = (b.total_price_mxn || 0) - (b.amount_paid || 0);
  const waHref = `https://wa.me/${(b.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(
    `Hola ${b.name}, te escribimos de Huitzil-Coatl Travels sobre tu reserva de "${b.tour_name}" para el ${b.date}.`
  )}`;

  const apply = (updated) => {
    onUpdated(updated);
    setOps(updated.ops || {});
    setNotes(updated.internal_notes || "");
  };

  const changeStatus = async (status) => {
    try {
      apply(await updateBookingStatus(b.id, status));
      toast.success(`Estado: ${STATUSES[status].label}`);
    } catch {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const saveOpsNotes = async () => {
    try {
      apply(await updateBooking(b.id, { ops, internal_notes: notes }));
      toast.success("Cambios guardados");
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const registerPayment = async (e) => {
    e.preventDefault();
    const amount = Number(payAmount);
    if (!amount || amount <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    try {
      apply(await addBookingPayment(b.id, { amount, method: payMethod, note: payNote }));
      toast.success(`Pago registrado: ${fmtMxn(amount)}`);
      setPayOpen(false);
      setPayAmount("");
      setPayNote("");
    } catch {
      toast.error("No se pudo registrar el pago");
    }
  };

  const runConfirm = async () => {
    try {
      if (confirmAction === "cancel") {
        apply(await updateBookingStatus(b.id, "cancelled"));
        toast.success("Reserva cancelada");
      } else if (confirmAction === "delete") {
        await deleteBooking(b.id);
        toast.success("Reserva eliminada");
        onUpdated(null, b.id);
        onClose();
      }
    } catch {
      toast.error("No se pudo completar la acción");
    } finally {
      setConfirmAction(null);
    }
  };

  const teamByRole = (role) => team.filter((m) => m.role === role);
  const row = "flex justify-between gap-4 py-2 border-b border-[#1E2B25] text-sm";
  const lbl = "text-[#A3A095]";
  const val = "text-[#F5F2EA] text-right";

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent data-testid="booking-detail" side="right" className="w-full sm:max-w-xl bg-[#080D0B] border-l border-[#1E2B25] overflow-y-auto p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#1E2B25]">
            <div className="flex items-start justify-between gap-4 pr-8">
              <div>
                <SheetTitle className="font-serif-hc text-2xl text-[#F5F2EA] leading-tight">{b.tour_name}</SheetTitle>
                <SheetDescription className="text-xs text-[#A3A095] mt-1">Reserva recibida el {(b.created_at || "").slice(0, 10)}</SheetDescription>
              </div>
              <StatusBadge status={b.status} />
            </div>
            {/* Quick actions */}
            <div className="flex flex-wrap gap-2 mt-4">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="detail-wa" className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.12em] uppercase border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 transition-colors">
                <MessageCircle size={13} /> WhatsApp
              </a>
              <button data-testid="detail-pay" onClick={() => setPayOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.12em] uppercase border border-[#C4A661]/40 text-[#C4A661] hover:bg-[#C4A661]/10 transition-colors">
                <Wallet size={13} /> Registrar pago
              </button>
              <button data-testid="detail-cancel" onClick={() => setConfirmAction("cancel")} disabled={b.status === "cancelled"} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.12em] uppercase border border-[#8B8778]/40 text-[#A3A095] hover:bg-[#8B8778]/10 disabled:opacity-40 transition-colors">
                <Ban size={13} /> Cancelar
              </button>
              <button data-testid="detail-delete" onClick={() => setConfirmAction("delete")} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.12em] uppercase border border-red-900/60 text-red-400/80 hover:bg-red-900/20 transition-colors">
                <Trash2 size={13} /> Eliminar
              </button>
            </div>
          </SheetHeader>

          <div className="px-6 py-5 space-y-7">
            {/* Estado */}
            <div>
              <p className="eyebrow mb-2">Estado</p>
              <select
                data-testid="detail-status"
                value={b.status}
                onChange={(e) => changeStatus(e.target.value)}
                className={inputCls}
                style={{ color: STATUSES[b.status]?.color }}
              >
                {Object.entries(STATUSES).map(([k, s]) => (
                  <option key={k} value={k} className="bg-[#080D0B] text-[#F5F2EA]">{s.label}</option>
                ))}
              </select>
            </div>

            {/* Cliente */}
            <div>
              <p className="eyebrow mb-2">Cliente</p>
              <div className={row}><span className={lbl}>Nombre</span><span className={val}>{b.name}</span></div>
              <div className={row}><span className={lbl}>Email</span><a href={`mailto:${b.email}`} className="text-[#139090] hover:underline">{b.email}</a></div>
              <div className={row}><span className={lbl}>WhatsApp</span><a href={waHref} target="_blank" rel="noreferrer" className="text-[#25D366] hover:underline">{b.whatsapp}</a></div>
            </div>

            {/* Reserva */}
            <div>
              <p className="eyebrow mb-2">Reserva</p>
              <div className={row}><span className={lbl}>Fecha</span><span className={val}>{b.date}</span></div>
              {b.schedule && <div className={row}><span className={lbl}>Horario</span><span className={val}>{b.schedule}</span></div>}
              <div className={row}><span className={lbl}>Personas</span><span className={val}>{b.adults} adultos / {b.children} niños</span></div>
              <div className={row}>
                <span className={lbl}>Extras</span>
                <span className={val}>{b.extras?.length ? b.extras.map((e) => EXTRA_LABELS[e] || e).join(" + ") : "—"}</span>
              </div>
              {b.comments && <div className={row}><span className={lbl}>Comentarios</span><span className={val}>{b.comments}</span></div>}
            </div>

            {/* Pago */}
            <div>
              <p className="eyebrow mb-2">Pago</p>
              <div className={row}><span className={lbl}>Total</span><span className="font-serif-hc text-lg text-[#C4A661]">{fmtMxn(b.total_price_mxn)}</span></div>
              <div className={row}><span className={lbl}>Pagado</span><span className={val} data-testid="detail-paid">{fmtMxn(b.amount_paid)}</span></div>
              <div className={row}>
                <span className={lbl}>Saldo</span>
                <span data-testid="detail-balance" className={balance > 0 ? "text-[#C4A661]" : "text-[#7BC47F]"}>{fmtMxn(balance)}</span>
              </div>
              {(b.payments || []).length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {b.payments.map((p) => (
                    <p key={p.id} className="text-xs text-[#A3A095]">
                      {(p.at || "").slice(0, 10)} · <span className="text-[#F5F2EA]">{fmtMxn(p.amount)}</span> · {p.method}{p.note ? ` · ${p.note}` : ""}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Operación */}
            <div>
              <p className="eyebrow mb-3">Operación</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OPS_FIELDS.map((f) => (
                  <label key={f.key} className={f.key === "meeting_point" ? "sm:col-span-2" : ""}>
                    <span className="block text-[10px] tracking-[0.18em] uppercase text-[#A3A095] mb-1.5">{f.label}</span>
                    <input
                      data-testid={`detail-ops-${f.key}`}
                      value={ops[f.key] || ""}
                      onChange={(e) => setOps((o) => ({ ...o, [f.key]: e.target.value }))}
                      list={["guide", "photographer", "transport"].includes(f.key) ? `team-${f.key}` : undefined}
                      className={inputCls}
                    />
                  </label>
                ))}
              </div>
              <datalist id="team-guide">{teamByRole("guia").map((m) => <option key={m.id} value={m.name} />)}</datalist>
              <datalist id="team-photographer">{teamByRole("fotografo").map((m) => <option key={m.id} value={m.name} />)}</datalist>
              <datalist id="team-transport">{teamByRole("transporte").map((m) => <option key={m.id} value={m.name} />)}</datalist>
            </div>

            {/* Notas internas */}
            <div>
              <p className="eyebrow mb-2">Notas internas</p>
              <textarea
                data-testid="detail-notes"
                rows="3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`${inputCls} resize-none`}
                placeholder="Solo visible para el equipo…"
              />
              <button data-testid="detail-save" onClick={saveOpsNotes} className="hc-btn-primary mt-3 inline-flex items-center gap-2">
                <Save size={14} /> Guardar cambios
              </button>
            </div>

            {/* Historial */}
            <div>
              <p className="eyebrow mb-3">Historial</p>
              <div className="relative pl-4 border-l border-[#1E2B25] space-y-3" data-testid="detail-history">
                {(b.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 bg-[#C4A661] rounded-full" />
                    <p className="text-xs text-[#A3A095]">{(h.at || "").slice(0, 16).replace("T", " · ")}</p>
                    <p className="text-sm text-[#F5F2EA]">{h.event}</p>
                  </div>
                ))}
                {!(b.history || []).length && <p className="text-sm text-[#A3A095]">Sin eventos registrados.</p>}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Registrar pago */}
      <Sheet open={payOpen} onOpenChange={setPayOpen}>
        <SheetContent data-testid="payment-dialog" side="bottom" className="bg-[#080D0B] border-t border-[#1E2B25] max-w-xl mx-auto max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-serif-hc text-2xl text-[#F5F2EA]">Registrar pago</SheetTitle>
            <SheetDescription className="text-xs text-[#A3A095]">
              Total {fmtMxn(b.total_price_mxn)} · Saldo pendiente {fmtMxn(balance)}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={registerPayment} className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label>
              <span className="block text-[10px] tracking-[0.18em] uppercase text-[#A3A095] mb-1.5">Monto (MXN)</span>
              <input data-testid="payment-amount" type="number" min="1" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className={inputCls} />
            </label>
            <label>
              <span className="block text-[10px] tracking-[0.18em] uppercase text-[#A3A095] mb-1.5">Método</span>
              <select data-testid="payment-method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className={inputCls}>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m} className="bg-[#080D0B]">{m}</option>)}
              </select>
            </label>
            <label>
              <span className="block text-[10px] tracking-[0.18em] uppercase text-[#A3A095] mb-1.5">Nota</span>
              <input data-testid="payment-note" value={payNote} onChange={(e) => setPayNote(e.target.value)} className={inputCls} />
            </label>
            <button data-testid="payment-submit" type="submit" className="hc-btn-primary sm:col-span-3">Registrar</button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Confirmaciones */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent data-testid="confirm-dialog" className="bg-[#0C1310] border border-[#1E2B25]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-hc text-2xl text-[#F5F2EA]">
              {confirmAction === "cancel" ? "¿Cancelar esta reserva?" : "¿Eliminar esta reserva?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A3A095]">
              {confirmAction === "cancel"
                ? `La reserva de ${b.name} pasará a estado Cancelada. Podrás reactivarla cambiando el estado.`
                : `Se eliminará permanentemente la reserva de ${b.name}. Esta acción no se puede deshacer.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-no" className="bg-transparent border border-[#1E2B25] text-[#A3A095] hover:bg-[#1E2B25] hover:text-[#F5F2EA]">Volver</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-yes" onClick={runConfirm} className="bg-red-900/80 text-[#F5F2EA] hover:bg-red-800">
              {confirmAction === "cancel" ? "Cancelar reserva" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
